import React, { useCallback, useEffect, useState, useRef } from "react";
import { theme } from "../../../constants/StyledComponents";
import { BodyTyp } from "../../Typography/Typography";
import { FontWeightEnum } from "../../../types/AppTypes";
import {
  StyledMultiRangeContainer,
  StyledSlider,
  StyledSliderContainer,
  StyledSliderRange,
  StyledSliderTrack,
  StyledThumbLeft,
  StyledThumbRight,
  StyledTick,
  StyledTickContainer,
  StyledTooltip,
  StyledTooltipArrow,
} from "./MultiRangeSlider.styled";

interface MultiRangeSliderProps {
  value?: { min: number; max: number };
  onChange: (range: { min: number; max: number }) => void;
}

const ThumbTooltip = ({
  position,
  value,
}: {
  position: number;
  value: number;
}) => {
  return (
    <StyledTooltip $position={position}>
      <BodyTyp
        variant="2"
        fontWeight={FontWeightEnum.REGULAR}
        color={theme.colors.white}
      >
        {value}
      </BodyTyp>
      <StyledTooltipArrow />
    </StyledTooltip>
  );
};

const MultiRangeSlider = ({ onChange, value }: MultiRangeSliderProps) => {
  const min = 0;
  const max = 100;
  const step = 1;
  const [minVal, setMinVal] = useState<number>(value ? value.min : min);
  const [maxVal, setMaxVal] = useState<number>(value ? value.max : max);
  const minValRef = useRef<number>(value ? value.min : min);
  const maxValRef = useRef<number>(value ? value.max : max);
  const range = useRef<HTMLDivElement>(null);

  const getPercent = useCallback(
    (value: number) => Math.round(((value - min) / (max - min)) * 100),
    [min, max],
  );

  useEffect(() => {
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxValRef.current);

    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [maxVal, minVal, getPercent]);

  useEffect(() => {
    onChange({ min: minVal, max: maxVal });
  }, [minVal, maxVal, onChange]);

  const getLeftTooltipPosition = () => {
    return (minVal / 100) * 100;
  };

  const getRightTooltipPosition = () => {
    return (maxVal / 100) * 100;
  };

  return (
    <StyledMultiRangeContainer>
      <StyledSliderContainer>
        <ThumbTooltip position={getLeftTooltipPosition()} value={minVal} />
        <ThumbTooltip position={getRightTooltipPosition()} value={maxVal} />
        <StyledThumbLeft
          type="range"
          min={min}
          max={max}
          step={step}
          value={minVal}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const value = Math.min(Number(event.target.value), maxVal - step);
            setMinVal(value);
            minValRef.current = value;
          }}
          $zIndex={minVal > max - 100 ? 5 : undefined}
        />
        <StyledThumbRight
          type="range"
          min={min}
          max={max}
          step={step}
          value={maxVal}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const value = Math.max(Number(event.target.value), minVal + step);
            setMaxVal(value);
            maxValRef.current = value;
          }}
        />

        <StyledSlider>
          <StyledSliderTrack />
          <StyledSliderRange ref={range} />
        </StyledSlider>
      </StyledSliderContainer>
      <StyledTickContainer>
        {[...Array(21)].map((_, i) => (
          <StyledTick
            key={`tick-${i}`}
            data-label={i * 5}
            $isMiddle={i % 2 !== 0}
          />
        ))}
      </StyledTickContainer>
    </StyledMultiRangeContainer>
  );
};

export default MultiRangeSlider;
